@if (mode === 'link') {
  <div class="min-h-[60vh] flex items-center justify-center px-4 py-16">
    <div class="daisy-card bg-base-100 border border-base-200 shadow-md p-8 max-w-md w-full text-center space-y-4">
      <span class="daisy-loading daisy-loading-spinner daisy-loading-lg text-primary mx-auto"></span>
      <h2 class="text-xl font-bold text-base-content">Redirecting to Terms & Conditions...</h2>
      <p class="text-xs text-base-content/70">
        You are being redirected to {{ academyName }}'s external Terms and Conditions.
      </p>
      @if (externalUrl) {
        <div class="pt-2">
          <a [href]="externalUrl" class="daisy-btn daisy-btn-sm daisy-btn-primary gap-2">
            <span>Click here if not redirected</span>
            <i class="fa-solid fa-arrow-up-right-from-square text-xs"></i>
          </a>
        </div>
      }
    </div>
  </div>
} @else if (mode === 'custom') {
  <!-- Hero Banner -->
  <div class="bg-base-200 border-b border-base-300 py-12 px-6 text-center">
    <div class="max-w-3xl mx-auto">
      <span class="daisy-badge daisy-badge-outline text-[10px] font-bold tracking-widest uppercase py-3 px-4 mb-4 bg-base-100 border-base-300 text-base-content/70">
        LEGAL DOCUMENT
      </span>
      <h1 class="text-4xl font-extrabold text-base-content leading-tight mb-3">Terms and Conditions</h1>
      <p class="text-[15px] text-base-content/70 leading-relaxed max-w-lg mx-auto">
        Please read these terms carefully. They govern your use of {{ academyName }} courses and educational services.
      </p>
      @if (lastUpdated) {
        <p class="text-xs text-base-content/60 mt-3 flex items-center justify-center gap-1.5">
          <i class="fa-regular fa-calendar"></i>
          <span>Last Updated: {{ lastUpdated | date:'mediumDate' }}</span>
        </p>
      }
    </div>
  </div>

  <!-- Custom Page Body -->
  <div class="max-w-4xl mx-auto px-6 py-10 md:py-16">
    <div class="daisy-card bg-base-100 border border-base-200 shadow-sm rounded-2xl p-6 sm:p-10">
      <div class="ql-snow">
        <div class="ql-editor !p-0 max-w-none text-base-content leading-relaxed" [innerHTML]="customContent"></div>
      </div>
    </div>
  </div>
} @else {
  <!-- Hero Banner -->
  <div class="bg-base-200 border-b border-base-300 py-12 px-6 text-center">
    <div class="max-w-2xl mx-auto">
      <span class="daisy-badge daisy-badge-outline text-[10px] font-bold tracking-widest uppercase py-3 px-4 mb-4 bg-base-100 border-base-300 text-base-content/70">
        LEGAL DOCUMENT
      </span>
      <h1 class="text-4xl font-extrabold text-base-content leading-tight mb-3">Terms and Conditions</h1>
      <p class="text-[15px] text-base-content/70 leading-relaxed max-w-lg mx-auto">
        Please read these terms carefully before using our platform. They govern
        your use of {{ academyName }}'s online course subscription services.
      </p>
    </div>
  </div>

  <!-- Page Body -->
  <div class="flex flex-col md:flex-row gap-10 max-w-6xl mx-auto px-6 py-10 md:py-16 items-start">
    <!-- Left Sidebar -->
    <aside class="w-full md:w-60 md:shrink-0 md:sticky md:top-20">
      <div class="daisy-card bg-base-100 border border-base-200 shadow-sm rounded-xl p-6">
        <h2 class="text-base font-bold text-base-content mb-2">At a Glance</h2>
        <p class="text-xs text-base-content/70 leading-relaxed mb-4">
          These terms apply when you access our courses or create an account on
          {{ academyName }}.
        </p>

        <div class="flex flex-col gap-3 pb-4 border-b border-base-200 mb-4">
          <div class="flex gap-2.5 items-start text-xs text-base-content">
            <i class="fa-regular fa-calendar text-base-content/50 mt-0.5 shrink-0"></i>
            <div>
              <span class="block font-semibold text-base-content">Last Updated</span>
              <span class="block text-base-content/70">{{ (lastUpdated | date:'mediumDate') || 'June 2024' }}</span>
            </div>
          </div>
          <div class="flex gap-2.5 items-start text-xs text-base-content">
            <i class="fa-regular fa-circle-question text-base-content/50 mt-0.5 shrink-0"></i>
            <div>
              <span class="block font-semibold text-base-content">Questions?</span>
              <a [href]="'mailto:' + contactEmail" class="block text-primary hover:underline break-all"
                >{{ contactEmail }}</a
              >
            </div>
          </div>
        </div>

        <!-- Jump to Section -->
        <div>
          <h3 class="text-[10px] font-bold tracking-wider text-base-content/50 uppercase mb-2.5">JUMP TO SECTION</h3>
          <nav class="flex flex-col gap-1">
            <a href="terms#section-1" class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >1. Use of Website</a
            >
            <a href="terms#section-2" class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >2. Student Eligibility</a
            >
            <a href="terms#section-3" class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >3. Subscription Terms</a
            >
            <a href="terms#section-4" class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >4. Account Security</a
            >
            <a href="terms#section-5" class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >5. Course Content Usage</a
            >
            <a href="terms#section-6" class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >6. Student Conduct</a
            >
            <a href="terms#contact" class="block text-xs text-base-content/80 hover:bg-base-200 hover:text-base-content px-2 py-1.5 rounded-lg transition-colors duration-150"
              >Contact Information</a
            >
          </nav>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <main class="flex-1 min-w-0">
      <p class="text-sm text-base-content/80 leading-relaxed mb-8">
        These Terms and Conditions govern your use of {{ academyName }}'s online-course
        subscription platform. By accessing our courses or creating an account,
        you agree to comply with these terms.
      </p>

      <!-- Section 1 -->
      <section id="section-1" class="mb-8 scroll-mt-20">
        <h2 class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1">
          <span class="text-primary font-bold">1.</span> Use of Website
        </h2>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">
            You are responsible for maintaining the confidentiality of your
            account information, including your username and password.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            You agree to provide accurate and current information during the
            registration and checkout process.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            You may not use our website for any unlawful or unauthorized purposes.
          </li>
        </ul>
      </section>

      <!-- Section 2 -->
      <section id="section-2" class="mb-8 scroll-mt-20">
        <h2 class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1">
          <span class="text-primary font-bold">2.</span> Student Eligibility
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed">
          We don't require any specific qualifications to access our courses.
          However, students under 16 must have parental consent.
        </p>
      </section>

      <!-- Section 3 -->
      <section id="section-3" class="mb-8 scroll-mt-20">
        <h2 class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1">
          <span class="text-primary font-bold">3.</span> Subscription Terms
        </h2>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">Access to courses requires an active subscription.</li>
          <li class="text-sm text-base-content/80 leading-relaxed">Subscription fees are billed according to your chosen plan.</li>
          <li class="text-sm text-base-content/80 leading-relaxed">Payments are processed securely through our payment partners.</li>
          <li class="text-sm text-base-content/80 leading-relaxed">Refunds are subject to our Refund Policy.</li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            We may have right to reject your payments through bank transfer based
            on the fraud risk.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            We use trusted third-party payment processors to handle your payment
            information securely. We do not store or have access to your full
            payment details.
          </li>
          <li class="text-sm text-base-content/80 leading-relaxed">
            We reserve the right to refuse or cancel any subscription for any
            reason, including but not limited to product availability, errors in
            pricing or product information, or suspected fraudulent activity.
          </li>
        </ul>
      </section>

      <!-- Section 4 -->
      <section id="section-4" class="mb-8 scroll-mt-20">
        <h2 class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1">
          <span class="text-primary font-bold">4.</span> Account Security
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed">
          You are responsible for maintaining the confidentiality of your account
          credentials and all activities under your account. Sharing your account
          credentials with others is prohibited. We may suspend your account if we
          suspect any misuse of your account.
        </p>
      </section>

      <!-- Section 5 -->
      <section id="section-5" class="mb-8 scroll-mt-20">
        <h2 class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1">
          <span class="text-primary font-bold">5.</span> Course Content Usage
        </h2>
        <ul class="list-disc pl-5 flex flex-col gap-2">
          <li class="text-sm text-base-content/80 leading-relaxed">All course materials are for personal, educational use only.</li>
          <li class="text-sm text-base-content/80 leading-relaxed">Downloading, sharing, or redistributing content is prohibited.</li>
          <li class="text-sm text-base-content/80 leading-relaxed">Your progress and completion certificates are personal.</li>
        </ul>
      </section>

      <!-- Section 6 -->
      <section id="section-6" class="mb-8 scroll-mt-20">
        <h2 class="text-lg font-bold text-base-content mb-3 flex items-baseline gap-1">
          <span class="text-primary font-bold">6.</span> Student Code of Conduct
        </h2>
        <p class="text-sm text-base-content/80 leading-relaxed">
          Students must maintain respectful behavior in all course interactions,
          including discussions and live sessions. Any form of harassment or abuse
          will result in immediate account suspension.
        </p>
      </section>

      <!-- Contact Information -->
      <section id="contact" class="scroll-mt-20 mt-10 pt-8 border-t border-base-200">
        <h2 class="text-lg font-bold text-primary mb-1">Contact Information</h2>
        <p class="text-sm text-base-content/80 mb-5">
          For any questions about these terms, please contact our support team.
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <a [href]="'mailto:' + contactEmail" class="flex items-center gap-2.5 bg-base-100 border border-base-200 rounded-xl px-5 py-3 text-[13px] text-base-content transition duration-150 ease-in-out min-w-[200px] hover:border-primary hover:shadow-sm hover:text-primary">
            <i class="fa-regular fa-envelope text-base-content/50"></i>
            <span>{{ contactEmail }}</span>
          </a>
          <div class="flex items-center gap-2.5 bg-base-100 border border-base-200 rounded-xl px-5 py-3 text-[13px] text-base-content min-w-[200px]">
            <i class="fa-regular fa-clock text-base-content/50"></i>
            <span>Mon – Fri, 9 AM – 6 PM</span>
          </div>
        </div>
      </section>
    </main>
  </div>
}
